import { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { usePushNotifications } from "@/contexts/PushNotificationContext";
import { useRealTimeNotifications } from "@/hooks/useRealTimeNotifications";
import { 
  Bell, 
  BellRing,
  Inbox, 
  Gift, 
  RefreshCw, 
  Handshake, 
  MessageCircle, 
  TrendingUp, 
  FileText,
  Check,
  CheckCheck,
  X,
  Sparkles,
  Megaphone,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const iconMap = {
  "handshake": Handshake,
  "message-circle": MessageCircle,
  "trending-up": TrendingUp,
  "file-text": FileText,
  "gift": Gift,
  "sparkles": Sparkles,
  "megaphone": Megaphone
};

const getIcon = (iconName) => {
  return iconMap[iconName] || Bell;
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const NotificationItem = ({ notification, onMarkRead, onDelete }) => {
  const Icon = getIcon(notification.icon);
  
  return (
    <div 
      className={`flex items-start gap-3 p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
        !notification.read ? 'bg-blue-50/50' : ''
      }`}
    >
      <div className={`p-2 rounded-full ${
        notification.type === 'offer' ? 'bg-amber-100 text-amber-600' :
        notification.type === 'update' ? 'bg-purple-100 text-purple-600' :
        'bg-blue-100 text-blue-600'
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {notification.link ? (
              <Link to={notification.link} onClick={() => !notification.read && onMarkRead(notification.id)}>
                <p className={`text-sm font-medium text-slate-900 hover:text-blue-600 ${!notification.read ? 'font-semibold' : ''}`}>
                  {notification.title}
                </p>
              </Link>
            ) : (
              <p className={`text-sm font-medium text-slate-900 ${!notification.read ? 'font-semibold' : ''}`}>
                {notification.title}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
            <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(notification.created_at)}</p>
          </div>
          
          <div className="flex items-center gap-1">
            {!notification.read && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => onMarkRead(notification.id)}
                title="Mark as read"
              >
                <Check className="w-3 h-3" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 text-slate-400 hover:text-red-500"
              onClick={() => onDelete(notification.id)}
              title="Delete"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
      
      {!notification.read && (
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
      )}
    </div>
  );
};

const EmptyState = ({ type }) => {
  const messages = {
    inbox: { icon: Inbox, text: "No messages or deal updates" },
    offer: { icon: Gift, text: "No offers or promotions right now" },
    update: { icon: RefreshCw, text: "No system updates" }
  };
  const { icon: Icon, text } = messages[type] || messages.inbox;
  
  return (
    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
      <Icon className="w-10 h-10 mb-2" />
      <p className="text-sm">{text}</p>
    </div>
  );
};

export default function NotificationDropdown() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("inbox");
  const [notifications, setNotifications] = useState({ inbox: [], offer: [], update: [] });
  const [unreadCounts, setUnreadCounts] = useState({ inbox: 0, offer: 0, update: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  
  // Push notification context
  const { permissionStatus, isSupported, enablePushNotifications } = usePushNotifications();
  const [enablingPush, setEnablingPush] = useState(false);

  // Handle real-time notification
  const handleRealtimeNotification = useCallback((notification) => {
    // Add to notifications list
    setNotifications(prev => ({
      ...prev,
      [notification.type || 'inbox']: [notification, ...(prev[notification.type || 'inbox'] || [])]
    }));
    
    // Update unread counts
    setUnreadCounts(prev => ({
      ...prev,
      [notification.type || 'inbox']: (prev[notification.type || 'inbox'] || 0) + 1,
      total: (prev.total || 0) + 1
    }));
    
    // Trigger bell animation
    setHasNewNotification(true);
    setTimeout(() => setHasNewNotification(false), 3000);
    
    // Show toast
    toast(notification.title, {
      description: notification.message,
      action: notification.link ? {
        label: 'View',
        onClick: () => window.location.href = notification.link
      } : undefined
    });
  }, []);

  // Real-time WebSocket connection
  const { isConnected, unreadCount: wsUnreadCount } = useRealTimeNotifications(
    user,
    handleRealtimeNotification
  );

  // Update counts from WebSocket
  useEffect(() => {
    if (wsUnreadCount.total > 0) {
      setUnreadCounts(wsUnreadCount);
    }
  }, [wsUnreadCount]);

  // Handle enabling push notifications
  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      const token = await enablePushNotifications();
      if (token) {
        toast.success("Push notifications enabled!");
      } else if (permissionStatus === 'denied') {
        toast.error("Notifications blocked. Enable them in browser settings.");
      }
    } catch (error) {
      toast.error("Failed to enable notifications");
    } finally {
      setEnablingPush(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCounts();
    }
  }, [user]);

  useEffect(() => {
    if (open && user) {
      fetchNotifications(activeTab);
    }
  }, [open, activeTab, user]);

  const fetchUnreadCounts = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCounts(data);
    } catch (error) {
      console.error("Failed to fetch unread counts:", error);
    }
  };

  const fetchNotifications = async (type) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/notifications?notification_type=${type}`);
      setNotifications(prev => ({ ...prev, [type]: data }));
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      }));
      fetchUnreadCounts();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch(`/notifications/read-all?notification_type=${activeTab}`);
      setNotifications(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].map(n => ({ ...n, read: true }))
      }));
      fetchUnreadCounts();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].filter(n => n.id !== notificationId)
      }));
      fetchUnreadCounts();
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  if (!user) return null;

  const BellIcon = hasNewNotification ? BellRing : Bell;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <BellIcon className={`w-5 h-5 ${hasNewNotification ? 'animate-bounce text-orange-500' : ''}`} />
          {unreadCounts.total > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
              {unreadCounts.total > 9 ? "9+" : unreadCounts.total}
            </span>
          )}
          {isConnected && (
            <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full" title="Real-time connected" />
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Notifications</h3>
          {unreadCounts[activeTab] > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-blue-600 hover:text-blue-700"
              onClick={handleMarkAllRead}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Push notification prompt */}
        {isSupported && permissionStatus !== 'granted' && permissionStatus !== 'denied' && (
          <div className="px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-orange-500" />
                <span className="text-xs text-slate-700">Get notified even when away</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs bg-white hover:bg-orange-50 border-orange-200"
                onClick={handleEnablePush}
                disabled={enablingPush}
              >
                {enablingPush ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  "Enable"
                )}
              </Button>
            </div>
          </div>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-3 h-10 bg-slate-50 rounded-none border-b">
            <TabsTrigger value="inbox" className="relative text-xs data-[state=active]:bg-white">
              <Inbox className="w-3.5 h-3.5 mr-1" />
              Inbox
              {unreadCounts.inbox > 0 && (
                <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-blue-500">
                  {unreadCounts.inbox}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="offer" className="relative text-xs data-[state=active]:bg-white">
              <Gift className="w-3.5 h-3.5 mr-1" />
              Offers
              {unreadCounts.offer > 0 && (
                <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-amber-500">
                  {unreadCounts.offer}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="update" className="relative text-xs data-[state=active]:bg-white">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Updates
              {unreadCounts.update > 0 && (
                <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-purple-500">
                  {unreadCounts.update}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <ScrollArea className="h-80">
            {["inbox", "offer", "update"].map(type => (
              <TabsContent key={type} value={type} className="m-0">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : notifications[type].length === 0 ? (
                  <EmptyState type={type} />
                ) : (
                  notifications[type].map(notification => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkRead={handleMarkRead}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </TabsContent>
            ))}
          </ScrollArea>
        </Tabs>
        
        <div className="p-2 border-t border-slate-100 bg-slate-50">
          <Link to="/notifications" onClick={() => setOpen(false)}>
            <Button variant="ghost" size="sm" className="w-full text-xs">
              View All Notifications
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
